/**
 * Contract Helper Functions for TPHILE Smart Contracts
 * Utilities for interacting with UserVesting, TeamVesting, and TPhileToken contracts
 */

/* global BigInt */

import { ethers } from 'ethers';
import {
  VESTING_DURATION_SECONDS,
  TEAM_CLIFF_DURATION_SECONDS,
  TEAM_TOTAL_VESTING_SECONDS,
  TEAM_LINEAR_VESTING_SECONDS,
  SECONDS_PER_YEAR,
  PREDICTION_BASE_RATES
} from './vestingHelpers';

/**
 * Calculate user vesting progress from TGE timestamp
 * @param {bigint|number} tgeTimestamp - TGE timestamp in seconds
 * @param {number} currentTimestamp - Current timestamp in seconds (default: now)
 * @returns {number} Progress percentage (0-100)
 */
export const calculateUserVestingProgress = (tgeTimestamp, currentTimestamp = Math.floor(Date.now() / 1000)) => {
  if (!tgeTimestamp) return 0;
  
  const tge = Number(tgeTimestamp);
  
  if (currentTimestamp < tge) return 0;
  
  const elapsed = currentTimestamp - tge;
  if (elapsed >= VESTING_DURATION_SECONDS) return 100;
  
  return (elapsed / VESTING_DURATION_SECONDS) * 100;
};

/**
 * Calculate team vesting progress with cliff period
 * @param {bigint|number} tgeTimestamp - TGE timestamp in seconds
 * @param {number} currentTimestamp - Current timestamp in seconds (default: now)
 * @returns {{progress: number, cliffEnded: boolean, daysUntilCliff?: number}}
 */
export const calculateTeamVestingProgress = (tgeTimestamp, currentTimestamp = Math.floor(Date.now() / 1000)) => {
  if (!tgeTimestamp) return { progress: 0, cliffEnded: false };
  
  const tge = Number(tgeTimestamp);
  const elapsed = currentTimestamp - tge;
  
  // During cliff period (first 6 months): 0% progress
  if (elapsed < TEAM_CLIFF_DURATION_SECONDS) {
    return { 
      progress: 0, 
      cliffEnded: false,
      daysUntilCliff: Math.ceil((TEAM_CLIFF_DURATION_SECONDS - elapsed) / 86400)
    };
  }
  
  // After total vesting period: 100% progress
  if (elapsed >= TEAM_TOTAL_VESTING_SECONDS) {
    return { progress: 100, cliffEnded: true };
  }
  
  // Linear vesting over 18 months after cliff
  const linearElapsed = elapsed - TEAM_CLIFF_DURATION_SECONDS;
  const progress = (linearElapsed / TEAM_LINEAR_VESTING_SECONDS) * 100;
  
  return { 
    progress: Math.min(100, Math.max(0, progress)), 
    cliffEnded: true 
  };
};

/**
 * Get current year and base rate based on TGE timestamp
 * @param {bigint|number} tgeTimestamp - TGE timestamp in seconds
 * @param {number} currentTimestamp - Current timestamp in seconds (default: now)
 * @returns {{year: number, rate: number}}
 */
export const getCurrentYearAndRate = (tgeTimestamp, currentTimestamp = Math.floor(Date.now() / 1000)) => {
  if (!tgeTimestamp) return { year: 1, rate: PREDICTION_BASE_RATES[0] };
  
  const tge = Number(tgeTimestamp);
  
  if (currentTimestamp < tge) return { year: 1, rate: PREDICTION_BASE_RATES[0] };
  
  const elapsed = currentTimestamp - tge;
  const yearsPassed = Math.floor(elapsed / SECONDS_PER_YEAR);
  const year = Math.min(yearsPassed + 1, 4); // Year 1-4
  const rate = PREDICTION_BASE_RATES[year - 1];
  
  return { year, rate };
};

/**
 * Calculate claimable vested amount for users
 * @param {object} userInfo - User reward info from contract
 * @param {bigint|number} tgeTimestamp - TGE timestamp in seconds
 * @param {number} currentTimestamp - Current timestamp in seconds (default: now)
 * @returns {bigint} Claimable amount in wei
 */
export const calculateClaimableVested = (userInfo, tgeTimestamp, currentTimestamp = Math.floor(Date.now() / 1000)) => {
  if (!userInfo || !tgeTimestamp || !userInfo.vestedAmount) return 0n;
  
  const tge = Number(tgeTimestamp);
  const vestingStart = Number(userInfo.vestingStart || tge);
  
  if (currentTimestamp < vestingStart) return 0n;
  
  const elapsed = currentTimestamp - vestingStart;
  const vestedAmount = BigInt(userInfo.vestedAmount);
  const alreadyClaimed = BigInt(userInfo.claimedVested || 0);
  
  // Linear vesting over 6 months
  const vestedSoFar = (vestedAmount * BigInt(Math.min(elapsed, VESTING_DURATION_SECONDS))) / BigInt(VESTING_DURATION_SECONDS);
  const claimable = vestedSoFar > alreadyClaimed ? vestedSoFar - alreadyClaimed : 0n;
  
  return claimable > 0n ? claimable : 0n;
};

/**
 * Format wei amount to readable string
 * @param {bigint|string|number} amount - Amount in wei
 * @param {number} decimals - Token decimals (default: 18)
 * @returns {string} Formatted amount
 */
export const formatEther = (amount, decimals = 18) => {
  if (!amount) return '0';
  
  try {
    const formatted = ethers.formatUnits(amount, decimals);
    const num = parseFloat(formatted);
    
    if (num >= 1000000) {
      return (num / 1000000).toFixed(2) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(2) + 'K';
    }
    return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
  } catch {
    return amount.toString();
  }
};

/**
 * Parse ether amount to wei
 * @param {string|number} amount - Amount in ether
 * @param {number} decimals - Token decimals (default: 18)
 * @returns {bigint} Amount in wei
 */
export const parseEther = (amount, decimals = 18) => {
  try {
    return ethers.parseUnits(amount.toString(), decimals);
  } catch {
    return 0n;
  }
};

/**
 * Get user vesting info from contract
 * @param {ethers.Contract} contract - UserVesting contract instance
 * @param {string} userAddress - User wallet address
 * @returns {Promise<object>} User vesting information
 */
export const getUserVestingInfo = async (contract, userAddress) => {
  try {
    const [tgeActivated, tgeTimestamp, userRewards, claimable] = await Promise.all([
      contract.tgeActivated(),
      contract.tgeTimestamp(),
      contract.userRewards(userAddress),
      contract.getUserClaimable(userAddress)
    ]);
    
    const now = Math.floor(Date.now() / 1000);
    const tge = Number(tgeTimestamp);
    const progress = calculateUserVestingProgress(tgeTimestamp, now);
    const { year, rate } = getCurrentYearAndRate(tgeTimestamp, now);
    
    return {
      tgeActivated,
      tgeTimestamp: tge,
      totalRewards: userRewards.totalEarned?.toString() || '0',
      instantRewards: userRewards.instantAmount?.toString() || '0',
      vestedRewards: userRewards.vestedAmount?.toString() || '0',
      instantClaimed: userRewards.claimedInstant?.toString() || '0',
      vestedClaimed: userRewards.claimedVested?.toString() || '0',
      availableInstant: claimable.instantClaimable?.toString() || '0',
      availableVested: claimable.vestedClaimable?.toString() || '0',
      vestingProgress: progress,
      isVestingComplete: progress >= 100,
      vestingStart: Number(userRewards.vestingStart || tge),
      vestingEnd: Number(userRewards.vestingEnd || tge + VESTING_DURATION_SECONDS),
      currentYear: year,
      currentBaseRate: rate
    };
  } catch (error) {
    console.error('Error fetching user vesting info:', error);
    return null;
  }
};

/**
 * Get team member vesting info from contract
 * @param {ethers.Contract} contract - TeamVesting contract instance
 * @param {string} memberAddress - Team member wallet address
 * @returns {Promise<object>} Team member vesting information
 */
export const getTeamVestingInfo = async (contract, memberAddress) => {
  try {
    const [vestingInfo, tgeTimestamp] = await Promise.all([
      contract.getVestingInfo(memberAddress),
      contract.vestingStartTime()
    ]);
    
    const now = Math.floor(Date.now() / 1000);
    const tge = Number(tgeTimestamp);
    const { progress, cliffEnded, daysUntilCliff } = calculateTeamVestingProgress(tgeTimestamp, now);
    
    return {
      memberAddress,
      totalAllocation: vestingInfo.allocation?.toString() || '0',
      claimedAmount: vestingInfo.claimed?.toString() || '0',
      claimableAmount: vestingInfo.claimable?.toString() || '0',
      vestingProgress: progress,
      cliffEnded,
      cliffEndTimestamp: tge + TEAM_CLIFF_DURATION_SECONDS,
      vestingEndTimestamp: tge + TEAM_TOTAL_VESTING_SECONDS,
      daysUntilCliff: daysUntilCliff || 0,
      monthsElapsed: Math.floor((now - tge) / (30 * 24 * 60 * 60))
    };
  } catch (error) {
    console.error('Error fetching team vesting info:', error);
    return null;
  }
};

export default {
  calculateUserVestingProgress,
  calculateTeamVestingProgress,
  getCurrentYearAndRate,
  calculateClaimableVested,
  formatEther,
  parseEther,
  getUserVestingInfo,
  getTeamVestingInfo
};

